import { ATTRIBUTE_OBJECTS, REPLY_FORMAT_FOR_TARGET, type AttributeObject, type CollectReplyFormat } from "@sagot/constants";
import type { CollectReplyStep, SnoozeStep } from "../types";
import { sanitizeKey } from "./collect-model";

/** Where a collected reply (or a chosen button's label) goes: a visitor field, a lead field, this run only, or a custom attribute. */
export const REPLY_TARGETS = ["visitor.name", "visitor.email", "visitor.phone", "lead", "var", "attribute"] as const;
export type ReplyTarget = (typeof REPLY_TARGETS)[number];
/** The targets a reply button's label can go to (backend `BUTTON_SAVE_TO_PATTERN`). */
export const BUTTON_TARGETS = ["var", "attribute"] as const satisfies readonly ReplyTarget[];

export interface ReplySave {
  target: ReplyTarget;
  /** The `<key>` of `lead.<key>` / `var.<key>` / `<object>.attr.<key>`; empty for the visitor fields. */
  key: string;
  /** The object of a custom attribute. */
  object: AttributeObject;
}

const ATTRIBUTE_TARGET = /^(visitor|conversation|lead)\.attr\.(.*)$/;
const DEFAULT_OBJECT: AttributeObject = "visitor";

/** `visitor.attr.grade` / `lead.grade` / `var.answer` / `visitor.email` as a target + key. */
export function replySaveOf(saveTo: string): ReplySave {
  const attribute = ATTRIBUTE_TARGET.exec(saveTo);
  const object = ATTRIBUTE_OBJECTS.find((candidate) => candidate === attribute?.[1]);
  if (attribute && object) return { target: "attribute", key: attribute[2] ?? "", object };
  const fixed = REPLY_TARGETS.find((target) => target === saveTo);
  if (fixed) return { target: fixed, key: "", object: DEFAULT_OBJECT };
  if (saveTo.startsWith("lead.")) return { target: "lead", key: saveTo.slice("lead.".length), object: DEFAULT_OBJECT };
  if (saveTo.startsWith("var.")) return { target: "var", key: saveTo.slice("var.".length), object: DEFAULT_OBJECT };
  return { target: "var", key: "", object: DEFAULT_OBJECT };
}

export function replySaveToOf({ target, key, object }: ReplySave): string {
  if (target === "attribute") return `${object}.attr.${key}`;
  return target === "lead" || target === "var" ? `${target}.${key}` : target;
}

/** A target that takes a key typed in (a lead field, a variable) rather than one picked from a list. */
export const isTypedKeyTarget = (target: ReplyTarget): target is "lead" | "var" => target === "lead" || target === "var";

/** The same save target with a new target kind: keyed targets keep a sensible key, fixed ones lose it. */
export function retarget(current: ReplySave, target: ReplyTarget, fallbackKey: string): string {
  const key = isTypedKeyTarget(target) ? (isTypedKeyTarget(current.target) && current.key ? current.key : sanitizeKey(fallbackKey)) : "";
  return replySaveToOf({ target, key, object: current.object });
}

/** The format a reply saved here must have, or `undefined` when any format fits (a name is free text). */
export const formatForTarget = (saveTo: string): CollectReplyFormat | undefined => REPLY_FORMAT_FOR_TARGET[saveTo];

export type ReplyProblem = "range" | "format";

/** What is wrong with the length limits or the format of a "Collect customer reply" step (the server refuses the same). */
export function replyProblems(step: Pick<CollectReplyStep, "min_length" | "max_length" | "format" | "save_to">): ReplyProblem[] {
  const problems: ReplyProblem[] = [];
  if (step.min_length > step.max_length) problems.push("range");
  const wanted = formatForTarget(step.save_to);
  if (wanted !== undefined && step.format !== wanted) problems.push("format");
  return problems;
}

/** Steps whose card the composer lock changes: the visitor answers them with buttons or their own fields. */
const LOCKED_CARD_TYPES: ReadonlySet<string> = new Set(["reply_buttons", "collect_data", "ask_rating", "show_booking", "show_lead_form"]);
/** Steps that ask for typing: the lock is lifted for them and stays lifted. */
const TYPING_TYPES: ReadonlySet<string> = new Set(["collect_reply", "let_assistant_answer", "enable_reply", "hand_to_person"]);

/** Whether a "Disable customer reply" step changes anything: a card the visitor answers follows it before typing is allowed again. */
export function lockHasEffect(following: readonly { type: string }[]): boolean {
  for (const step of following) {
    if (TYPING_TYPES.has(step.type)) return false;
    if (LOCKED_CARD_TYPES.has(step.type)) return true;
  }
  return false;
}

/** How a snooze is set: for a while, until a moment, or until the office opens. */
export function snoozeModeOf(step: Pick<SnoozeStep, "for" | "until" | "until_office_hours">): "for" | "until" | "until_office_hours" {
  if (step.until) return "until";
  if (step.until_office_hours) return "until_office_hours";
  return "for";
}

/** Today's date as `YYYY-MM-DD` in the machine's time zone, to start a new "until" at something sensible. */
export function todayText(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${String(now.getFullYear())}-${month}-${day}`;
}
